import { TTSVoiceInfo } from '@/shared/types/voiceProviders';

/** Parsed voice data for UI dropdowns */
interface ParsedVoice {
  voice_id: string;
  name: string;
  accent: string | null;
  previewUrl?: string;
}

const stripDescriptor = (fullName: string): string => {
  const dashIndex = fullName.indexOf(' - ');
  return dashIndex === -1 ? fullName : fullName.slice(0, dashIndex);
};

/**
 * Extracts a short display name from a full voice name.
 * - "Roger - Laid-Back, Casual" → "Roger"
 * - "Spiros Konstantinou" → "Spiros"
 * - "Maria" → "Maria"
 */
export const extractShortName = (fullName: string): string =>
  stripDescriptor(fullName).split(' ')[0];

const DISPLAY_NAME_TIERS: Array<(voice: TTSVoiceInfo) => string> = [
  (voice) => extractShortName(voice.name),
  (voice) => stripDescriptor(voice.name),
  (voice) => voice.name,
  (voice) => (voice.description ? `${voice.name} · ${voice.description}` : voice.name),
];

/**
 * Transforms a list of TTSVoiceInfo objects into parsed voice options for UI selection.
 *
 * Voices are expected to be pre-filtered by language (via the hook's getVoicesByProvider).
 * Accents with the value "standard" are hidden (set to null).
 *
 * Each voice takes the first name in DISPLAY_NAME_TIERS no other voice in the list produces, so
 * two distinct voices never show the same label.
 *
 * @param voices - The list of TTS voices to process (already filtered by provider/language).
 * @returns An array of parsed voice objects containing id, display name, accent, and preview URL.
 */
export const voiceToOptions = (voices: TTSVoiceInfo[]): ParsedVoice[] => {
  const tierCounts = DISPLAY_NAME_TIERS.map((toLabel) => {
    const counts = new Map<string, number>();
    voices.forEach((voice) => {
      const label = toLabel(voice);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    });
    return counts;
  });

  const toDisplayName = (voice: TTSVoiceInfo): string => {
    const unambiguous = DISPLAY_NAME_TIERS.map((toLabel) => toLabel(voice)).find(
      (label, tier) => tierCounts[tier].get(label) === 1
    );
    return unambiguous ?? `${voice.name} (${voice.id.slice(0, 6)})`;
  };

  return voices.map((voice) => ({
    voice_id: voice.id,
    name: toDisplayName(voice),
    accent: voice.accent === 'standard' ? null : (voice.accent ?? null),
    previewUrl: voice.previewUrl,
  }));
};
